/*
 * Copyright 2026 Sony Corporation
 */
import {
  ACCESS_AUTH_MODELS,
  CameraLoginPasswordSchema,
  CameraLoginUserSchema,
  DEFAULT_CAMERA_LOGIN,
  type CameraLogin,
  type CameraLoginInput,
  type Device,
} from '@ips-jit/schema';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { ChevronIcon } from './icons';

/** One login row as the form holds it; an empty password keeps the saved one. */
export interface LoginDraft {
  user: string;
  password: string;
  saved: boolean;
}

/** The login fields' state: one login for every camera, or one per address. */
export interface LoginsDraft {
  open: boolean;
  shared: boolean;
  rows: Record<string, LoginDraft>;
  /** Addresses whose remembered SSH fingerprint is to be forgotten on save. */
  forget: string[];
}

/** The fields' first state: open when a model has [Access Authen.] or the runtime already has logins. */
export const initialLogins = (models: readonly string[], saved: Record<string, CameraLogin> = {}): LoginsDraft => {
  const keys = Object.keys(saved);
  const rows = Object.fromEntries(
    keys.map((key) => [key, { user: saved[key].user, password: '', saved: saved[key].has_password }])
  );
  return {
    open: keys.length > 0 || models.some((model) => ACCESS_AUTH_MODELS.includes(model)),
    shared: keys.length === 0 || keys.includes(DEFAULT_CAMERA_LOGIN),
    rows,
    forget: [],
  };
};

/**
 * What to send for the logins, or the first problem with them. Rows without a user are not logins; logins that were
 * saved and are now gone are removed (null).
 */
export const loginsToSend = (
  draft: LoginsDraft,
  addresses: string[],
  saved: Record<string, CameraLogin> = {}
): { camera_logins: Record<string, CameraLoginInput | null> } | { error: string } => {
  const keys = draft.shared ? [DEFAULT_CAMERA_LOGIN] : addresses;
  const out: Record<string, CameraLoginInput | null> = {};
  for (const key of keys) {
    const row = draft.rows[key];
    if (!row || row.user.trim() === '') {
      continue;
    }
    const where = key === DEFAULT_CAMERA_LOGIN ? 'Login' : `Login of ${key}`;
    const user = CameraLoginUserSchema.safeParse(row.user.trim());
    if (!user.success) {
      return { error: `${where}: ${user.error.issues[0]?.message}` };
    }
    if (row.password === '' && !row.saved) {
      return { error: `${where}: enter the password shown in [Access Authen. Info].` };
    }
    if (row.password !== '') {
      const password = CameraLoginPasswordSchema.safeParse(row.password);
      if (!password.success) {
        return { error: `${where}: ${password.error.issues[0]?.message}` };
      }
    }
    out[key] = { user: user.data, ...(row.password ? { password: row.password } : {}) };
  }
  for (const key of Object.keys(saved)) {
    if (!(key in out)) {
      out[key] = null;
    }
  }
  return { camera_logins: out };
};

const PasswordField = ({
  label,
  value,
  saved,
  onChange,
}: {
  label: string;
  value: string;
  saved: boolean;
  onChange: (value: string) => void;
}) => {
  const [shown, setShown] = useState(false);
  return (
    <TextField
      label={label}
      size="small"
      type={shown ? 'text' : 'password'}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={saved ? 'Saved. Type to replace it' : undefined}
      autoComplete="new-password"
      sx={{ flex: 1 }}
      slotProps={{
        htmlInput: { maxLength: 16 },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <Button size="small" onClick={() => setShown(!shown)} sx={{ minWidth: 0 }}>
                {shown ? 'Hide' : 'Show'}
              </Button>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

/**
 * Logins for cameras with access authentication ([Access Authen.] On, PTP over SSH). Folded unless a chosen model has
 * the setting; a camera without it needs nothing here. The camera shows what to type under Access Authen. Info.
 */
export const CameraLoginFields = ({
  draft,
  onChange,
  addresses,
  cameras = [],
}: {
  draft: LoginsDraft;
  onChange: (draft: LoginsDraft) => void;
  addresses: string[];
  /** The runtime's reported cameras, for the fingerprints they were seen with. */
  cameras?: Device['cameras'];
}) => {
  const keys = draft.shared ? [DEFAULT_CAMERA_LOGIN] : addresses;
  const row = (key: string): LoginDraft => draft.rows[key] ?? { user: '', password: '', saved: false };
  const set = (key: string, change: Partial<LoginDraft>) =>
    onChange({ ...draft, rows: { ...draft.rows, [key]: { ...row(key), ...change } } });
  // A camera that refused the login may have a new fingerprint (reset or replaced): it can be forgotten too.
  const fingerprints = cameras.filter((camera) => camera.fingerprint || camera.state === 'login_failed');

  return (
    <Box component="fieldset" sx={{ border: 0, p: 0, m: 0 }} aria-label="Camera login">
      <Button
        size="small"
        onClick={() => onChange({ ...draft, open: !draft.open })}
        aria-expanded={draft.open}
        startIcon={<ChevronIcon direction={draft.open ? 'up' : 'down'} fontSize="small" />}
        sx={{ textTransform: 'none', px: 0.5 }}
      >
        Cameras with access authentication (SSH) need a login
      </Button>
      <Collapse in={draft.open}>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            When [Access Authen.] is On, the camera only connects with its user and password. On the camera: MENU →
            Network → Network Option → Access Authen. Info. Cameras without it need nothing here.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={draft.shared}
                onChange={(event) => onChange({ ...draft, shared: event.target.checked })}
              />
            }
            label="Same login for all cameras"
          />
          {!draft.shared && addresses.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Enter the camera IP addresses first, or use the same login for all cameras.
            </Typography>
          )}
          {keys.map((key) => (
            <Stack key={key} spacing={0.5}>
              {key !== DEFAULT_CAMERA_LOGIN && <Typography variant="caption">{key}</Typography>}
              <Stack direction="row" spacing={1}>
                <TextField
                  label="User"
                  size="small"
                  value={row(key).user}
                  onChange={(event) => set(key, { user: event.target.value })}
                  autoComplete="off"
                  slotProps={{ htmlInput: { maxLength: 16, 'aria-label': `User${key === '*' ? '' : ` of ${key}`}` } }}
                  sx={{ flex: 1 }}
                />
                <PasswordField
                  label="Password"
                  value={row(key).password}
                  saved={row(key).saved}
                  onChange={(password) => set(key, { password })}
                />
                {(row(key).user || row(key).saved) && (
                  <IconButton
                    size="small"
                    aria-label={`Remove the login${key === '*' ? '' : ` of ${key}`}`}
                    onClick={() =>
                      onChange({ ...draft, rows: { ...draft.rows, [key]: { user: '', password: '', saved: false } } })
                    }
                  >
                    ×
                  </IconButton>
                )}
              </Stack>
            </Stack>
          ))}
          {fingerprints.length > 0 && (
            <Stack spacing={0.5} aria-label="Fingerprints">
              <Typography variant="caption" color="text.secondary">
                Fingerprints seen at the first login. They must match Access Authen. Info on the camera.
              </Typography>
              {fingerprints.map((camera) => (
                <Typography key={camera.address} variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {camera.address} {camera.fingerprint ?? '(login failed)'}{' '}
                  {draft.forget.includes(camera.address) ? (
                    '(forgotten on save)'
                  ) : (
                    <Link
                      component="button"
                      type="button"
                      onClick={() => onChange({ ...draft, forget: [...draft.forget, camera.address] })}
                    >
                      Forget
                    </Link>
                  )}
                </Typography>
              ))}
            </Stack>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};
